import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { SCHOOLS } from '../Schools/tokens';
import { formatLastGenerated } from '../Reports/lastGenerated';
import { fetchUploadDistricts, UNKNOWN_PARTITION, type UploadDistrictRow } from './uploadsAdminApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export function uploadRowLabel(row: UploadDistrictRow): string {
  if (row.districtName) return row.districtName;
  if (row.sandboxEmail) return `${row.sandboxEmail} (sandbox)`;
  return row.partition;
}

type Props = {
  source?: () => Promise<UploadDistrictRow[]>;
};

const bodyText = {
  fontFamily: SCHOOLS.font,
  fontSize: 16,
  lineHeight: '24px',
  color: SCHOOLS.muted,
} as const;

export function AdminUploadsPage({ source = fetchUploadDistricts }: Props = {}) {
  const [rows, setRows] = useState<UploadDistrictRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    source()
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return (
    <Box sx={{ display: 'flex', bgcolor: SCHOOLS.surface, minHeight: HEIGHT_BELOW_BARS }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '24px 32px' }}>
        <Typography
          component="h1"
          sx={{ fontFamily: SCHOOLS.font, fontSize: 24, fontWeight: 700, color: SCHOOLS.ink }}
        >
          Uploaded Files
        </Typography>
        <Typography
          data-testid="admin-uploads-provenance"
          sx={{ ...bodyText, fontSize: 14, lineHeight: '20px', mb: 2 }}
        >
          Every district with stored uploads. Counts and dates come from the files a district
          currently holds, so deleted files are excluded; open a district to see them.
        </Typography>
        {failed && (
          <Typography data-testid="admin-uploads-error" sx={bodyText}>
            Could not load uploads. Refresh to try again.
          </Typography>
        )}
        {!failed && rows === null && (
          <CircularProgress data-testid="admin-uploads-loading" size={24} />
        )}
        {!failed && rows !== null && rows.length === 0 && (
          <Typography data-testid="admin-uploads-empty" sx={bodyText}>
            No uploads have been recorded yet.
          </Typography>
        )}
        {!failed && rows !== null && rows.length > 0 && <DistrictsTable rows={rows} />}
      </Box>
    </Box>
  );
}

const HEADERS = ['District', 'Uploads', 'Last upload'];

const cellSx = {
  fontFamily: SCHOOLS.font,
  fontSize: 14,
  color: SCHOOLS.ink,
  borderBottom: `1px solid ${SCHOOLS.border}`,
} as const;

function DistrictsTable({ rows }: { rows: UploadDistrictRow[] }) {
  return (
    <Table
      size="small"
      sx={{ bgcolor: SCHOOLS.cardBg, borderRadius: '4px', boxShadow: SCHOOLS.cardShadow }}
    >
      <TableHead>
        <TableRow>
          {HEADERS.map((label) => (
            <TableCell key={label} sx={{ ...cellSx, fontWeight: 700 }}>
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.partition} data-testid="admin-uploads-row">
            <TableCell sx={cellSx}>
              {row.partition === UNKNOWN_PARTITION ? (
                uploadRowLabel(row)
              ) : (
                <Link
                  component={RouterLink}
                  to={`/admin/uploads/${encodeURIComponent(row.partition)}`}
                >
                  {uploadRowLabel(row)}
                </Link>
              )}
            </TableCell>
            <TableCell sx={cellSx}>{row.uploads}</TableCell>
            <TableCell sx={cellSx}>
              {(row.lastUploadedAt && formatLastGenerated(row.lastUploadedAt)) || '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
